import Button from '@mui/material/Button';
import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type Local = 'en' | 'fr';

interface InstallPWAButtonProps {
  local: Local;
}

const translations: Record<Local, any> = {
  en: {
    buttonLabel: 'Add to Home Screen',
    iosInstructions:
      'To install on your iOS device:\n1. Tap the "Share" icon in your browser.\n2. Scroll down and tap "Add to Home Screen".',
    androidPromptInstructions: {
      accepted: '', // no message needed if accepted
      dismissed:
        'Installation cancelled.\nTo install manually:\n1. Tap the menu (⋮) in your browser.\n2. Tap "Install app" or "Add to Home screen".',
      error:
        'If installation did not start:\n1. Tap the menu (⋮) in your browser.\n2. Tap "Install app" or "Add to Home screen".',
    },
    androidNoPrompt:
      'To install this app:\n1. Tap the menu (⋮) in your browser.\n2. Look for "Install app" or "Add to Home Screen".',
    otherInstructions:
      'To add this app to your home screen, please look for an "Add to Home Screen" or "Install" option in your browser\'s menu.',
    gotIt: 'Got it',
  },
  fr: {
    buttonLabel: 'Ajouter à l’écran d’accueil',
    iosInstructions:
      'Pour installer sur votre appareil iOS :\n1. Appuyez sur l’icône « Partager » dans votre navigateur.\n2. Faites défiler vers le bas et appuyez sur « Ajouter à l’écran d’accueil ».',
    androidPromptInstructions: {
      accepted: '',
      dismissed:
        'Installation annulée.\nPour l’installer manuellement :\n1. Appuyez sur le menu (⋮) dans votre navigateur.\n2. Appuyez sur « Installer l’application » ou « Ajouter à l’écran d’accueil ».',
      error:
        'Si l’installation n’a pas démarré :\n1. Appuyez sur le menu (⋮) dans votre navigateur.\n2. Appuyez sur « Installer l’application » ou « Ajouter à l’écran d’accueil ».',
    },
    androidNoPrompt:
      'Pour installer cette application :\n1. Appuyez sur le menu (⋮) dans votre navigateur.\n2. Recherchez « Installer l’application » ou « Ajouter à l’écran d’accueil ».',
    otherInstructions:
      'Pour ajouter cette application à votre écran d’accueil, veuillez rechercher une option « Ajouter à l’écran d’accueil » ou « Installer » dans le menu de votre navigateur.',
    gotIt: 'Compris',
  },
};

const InstallPWAButton: React.FC<InstallPWAButtonProps> = ({ local }) => {
  const t = translations[local];

  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>(
    'other'
  );
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionMessage, setInstructionMessage] = useState('');

  // Detect PWA installation state and platform
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true
      ) {
        setIsAppInstalled(true);
      }

      const ua = window.navigator.userAgent.toLowerCase();
      const isIosDevice =
        /iphone|ipad|ipod/.test(ua) ||
        (navigator.platform === 'MacIntel' &&
          navigator.maxTouchPoints > 1 &&
          !(window as any).MSStream);

      if (isIosDevice) {
        setPlatform('ios');
      } else if (/android/.test(ua)) {
        setPlatform('android');
      } else {
        setPlatform('other');
      }
    }
  }, []);

  // Listen for beforeinstallprompt event (Android/other)
  useEffect(() => {
    if (isAppInstalled) return undefined;
    if (platform === 'ios') return undefined;

    if (platform === 'android' || platform === 'other') {
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return undefined;
  }, [isAppInstalled, platform]);

  const handleInstallClick = async () => {
    setShowInstructions(true);

    if (platform === 'ios') {
      setInstructionMessage(t.iosInstructions);
      return;
    }

    if (platform === 'android' && deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsAppInstalled(true);
          setShowInstructions(false);
        } else {
          setInstructionMessage(t.androidPromptInstructions.dismissed);
        }
      } catch {
        setInstructionMessage(t.androidPromptInstructions.error);
      }
      setDeferredPrompt(null);
    } else if (platform === 'android' && !deferredPrompt) {
      setInstructionMessage(t.androidNoPrompt);
    } else if (platform === 'other') {
      setInstructionMessage(t.otherInstructions);
    }
  };

  if (isAppInstalled) {
    return null;
  }

  const canAttemptInstall =
    platform === 'ios' ||
    platform === 'android' ||
    (platform === 'other' && deferredPrompt);

  return (
    <>
      <Button
        variant="contained"
        style={{
          backgroundColor: '#0aa961',
          marginBottom: 12,
          color: 'white',
        }}
        onClick={handleInstallClick}
        disabled={!canAttemptInstall}
      >
        {t.buttonLabel}
      </Button>

      {showInstructions && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: '#f9f9f9',
            border: '1px solid #ccc',
            borderRadius: 4,
            maxWidth: 300,
            textAlign: 'left',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.9rem', whiteSpace: 'pre-line' }}>
            {instructionMessage}
          </p>
          <Button
            size="small"
            style={{ marginTop: 12, backgroundColor: '#bbb', color: '#fff' }}
            onClick={() => setShowInstructions(false)}
          >
            {t.gotIt}
          </Button>
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;
